/**
 * Shell panel: connects the Terminal component to a GuruConnect agent's
 * remote shell via the relay WebSocket.
 *
 * Uses a minimal hand-rolled protobuf encoder/decoder for the shell
 * message subset (no third-party protobuf library needed).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Terminal, type TerminalHandle } from "./Terminal";
import { Button } from "../../components/ui/Button";
import { decodeShellMessage, encodeShellRequest, encodeShellInput, encodeShellResize, encodeShellClose } from "./proto";
import "./shell-panel.css";

export type ShellType = "powershell" | "cmd";

export interface ShellPanelProps {
  /** WebSocket URL for the viewer relay connection (carries no credentials) */
  wsUrl: string;
  /**
   * Session-scoped viewer token. Sent in the WebSocket subprotocol offer
   * rather than the URL (GC10) -- a browser cannot set request headers on a
   * WebSocket, and query strings are recorded by proxies and history.
   */
  wsToken: string;
  /** Unique ID for this shell session */
  shellId: string;
  /** Shell type to launch on the agent */
  shellType: ShellType;
  /** Called when the shell session closes */
  onClose?: (exitCode: number | null) => void;
}

/** Subprotocol version marker the relay selects; see VIEWER_SUBPROTOCOL server-side. */
const VIEWER_SUBPROTOCOL = "guruconnect.viewer.v1";

export function ShellPanel({
  wsUrl,
  wsToken,
  shellId,
  shellType,
  onClose,
}: ShellPanelProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const termRef = useRef<TerminalHandle>(null);
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState("Connecting...");
  const decoder = useRef(new TextDecoder());

  // Callers commonly pass an inline arrow for onClose, which changes identity
  // on every parent render. Holding it in a ref keeps it out of the socket
  // effect's dependencies -- otherwise the sessions list refetch (every 8s)
  // would tear down the WebSocket and respawn the shell on the agent.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Connect WebSocket and send ShellRequest
  useEffect(() => {
    // The token rides in the subprotocol offer; the relay reads the `auth.`
    // entry and selects only the version marker, so the token is never echoed
    // back in the response headers.
    const ws = new WebSocket(wsUrl, [VIEWER_SUBPROTOCOL, `auth.${wsToken}`]);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("Opening shell...");
      ws.send(encodeShellRequest(shellId, shellType, 120, 30));
      setConnected(true);
      setStatus(shellType === "cmd" ? "CMD" : "PowerShell");
    };

    ws.onmessage = (event) => {
      if (!(event.data instanceof ArrayBuffer)) return;
      const buf = new Uint8Array(event.data);
      const msg = decodeShellMessage(buf);
      if (!msg) return;

      if (msg.type === "output") {
        const text = decoder.current.decode(msg.data);
        termRef.current?.appendOutput(text);
      } else if (msg.type === "close") {
        setConnected(false);
        setStatus(`Exited (${msg.exitCode})`);
        onCloseRef.current?.(msg.exitCode);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setStatus("Disconnected");
    };

    ws.onerror = () => {
      setStatus("Connection error");
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(encodeShellClose(shellId, "user_closed"));
      }
      ws.close();
    };
  }, [wsUrl, wsToken, shellId, shellType]);

  const handleInput = useCallback(
    (data: string) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(encodeShellInput(shellId, data));
    },
    [shellId],
  );

  const handleResize = useCallback(
    (cols: number, rows: number) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(encodeShellResize(shellId, cols, rows));
    },
    [shellId],
  );

  return (
    <div className="gc-shell-panel">
      <div className="gc-shell-panel__header">
        <span className="gc-shell-panel__title">
          {shellType === "cmd" ? "CMD" : "PowerShell"} -- Remote Shell
        </span>
        <div className="gc-shell-panel__actions">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(encodeShellClose(shellId, "user_closed"));
              }
              onClose?.(null);
            }}
          >
            Close
          </Button>
        </div>
      </div>
      <Terminal
        ref={termRef}
        onInput={handleInput}
        onResize={handleResize}
        connected={connected}
        status={status}
      />
    </div>
  );
}
